import { ApexAxisChartSeries, ApexChart, ApexDataLabels, ApexFill, ApexLegend, ApexPlotOptions, ApexTooltip, ApexXAxis, ApexYAxis, ChartComponent } from 'ng-apexcharts';
import { ChangeDetectorRef, Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ConfigSystemParams } from 'src/app/core/consts';
import { ConfigSystemService } from 'src/app/core/services/utelize/system/config-system/config-system-service';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { WidgetSimActivityDTO } from 'src/app/core/models/system/widget/widget-sim-activity.dto';

export type ChartOptions={
  chart      : ApexChart;
  dataLabels : ApexDataLabels;
  fill       : ApexFill;
  legend     : ApexLegend;
  plotOptions: ApexPlotOptions;
  series     : ApexAxisChartSeries;
  tooltip    : ApexTooltip;
  xaxis      : ApexXAxis;
  yaxis      : ApexYAxis;
};

@Component({
  selector: 'app-timeline-activity-heatmap',
  templateUrl: './timeline-activity-heatmap.component.html',
  styleUrls: ['./timeline-activity-heatmap.component.scss'],
  encapsulation: ViewEncapsulation.None
})

export class ActivityHeatmapComponent implements OnInit {
  
  @ViewChild('simActivityChart', { static: false }) simActivityChartComponent: ChartComponent | undefined;

  get circleValues           () { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.FormTimelineSimActivityCircles, false); }
  get timelineDisplayVersion2() { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.TimelineDisplayVersion2,        false); }
  
  public chartOptions!    : Partial<ChartOptions> | undefined;
  public simActivityDates : string[]=[];
  public simActivitySeries: ApexAxisChartSeries=[];
    
  constructor(
    private changeDetectorRef  : ChangeDetectorRef,
    private configSystemService: ConfigSystemService,
    private exceptionsService  : ExceptionsService
  ) {
     
  }

  public ngOnInit(): void {
    try {
      
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public refreshHeatMap(data: WidgetSimActivityDTO[] | undefined): void {
    try {
      if (!data || data.length===0) {
        this.simActivitySeries=[];
        this.simActivityDates =[];
      
        this.simActivityChartComponent?.updateSeries([], true);
        this.simActivityChartComponent?.updateOptions({
          xaxis: {
            categories: []
          }
        }, false);
      
        return;
      }
  
      const timeSlotsSet=new Set<string>();
      const activityMap: { [key: string]: { x: string; y: number }[] } = {};
      const formattedDates: string[]=[];
  
      data.forEach((entry: any) => {
        const date         =new Date(entry.dateValue);
        const formattedDate=date.toLocaleDateString("en-GB");
  
        if (!formattedDates.includes(formattedDate)) {
          formattedDates.push(formattedDate);
        }
  
        Object.keys(entry).forEach((key) => {
          if (/^_\d{4}$/.test(key)) {
            const raw    =key.replace("_", "");
            const hours  =raw.substring(0, 2);
            const minutes=raw.substring(2);
            const time   =`${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}`;
            timeSlotsSet.add(time);
  
            if (!activityMap[time]) activityMap[time] = [];
            activityMap[time].push({
              x: formattedDate,
              y: entry[key]===null ? -1 : (entry[key] ? 1 : 0)
            });
          }
        });
      });
  
      const sortedTimeSlots = Array.from(timeSlotsSet)
        .sort((a, b) => {
          const toMinutes=(t: string) => {
            const [h, m]=t.split(":").map(Number);
            return h*60+m;
          };
          return toMinutes(a)-toMinutes(b);
        })
        .reverse();
  
      const newSeries=sortedTimeSlots.map((time) => ({
        name: time,
        data: activityMap[time]
      }));
  
      if (!this.simActivitySeries.length) {
        this.simActivitySeries=newSeries;
        this.simActivityDates =formattedDates;
  
        this.chartOptions={
          series: this.simActivitySeries,
          chart : {
            id     : 'sim-activity-heatmap',
            height : this.timelineDisplayVersion2 ? 650 : 800,
            type   : 'heatmap',
            zoom   : { enabled: false },
            toolbar: { show: false }
          },
          xaxis: {
            type      : 'category',
            categories: this.simActivityDates,
            labels    : {
              rotate: -45,
              style : { fontSize: '11px' },
              // formatter: (value: string) => {
              //   const day=value.split("/")[0];
              //   return parseInt(day) % 2===0 ? day : "";
              // }
              formatter: (value: string | undefined) => {
                if (typeof value !== 'string' || !value.includes('/')) return '';
                
                const parts=value.split('/');
                const day  =parseInt(parts[0]);
              
                return isNaN(day) ? '' : (day % 2 === 0 ? day.toString() : '');
              }
            }
          },
          yaxis: {
            reversed: true,
            labels  : {
              style: {
                fontSize  : '13px',
                fontFamily: `"Courier New", Courier, monospace`
              },
              offsetX: -25,
              align  : 'right'
            }
          },
          plotOptions: {
            heatmap: {
              radius              : this.circleValues ? 20 : 0,
              enableShades        : false,
              useFillColorAsStroke: true,
              shadeIntensity      : 0,
              colorScale          : {
                ranges: [
                  { 
                    from : 0,
                    to   : 0,
                    color: '#f06548',
                    name : 'No Usage'
                  },
                  {
                    from : 1,
                    to   : 1,
                    color: '#0ab39c',
                    name : 'Usage'
                  },
                  {
                    from : -1,
                    to   : -1,
                    color: '#d3d3d3',
                    name : 'No Data'
                  }
                ]
              }
            }
          },
          tooltip: {
            custom: ({ series, seriesIndex, dataPointIndex, w }) => {
              const value=series[seriesIndex][dataPointIndex];
              const date =w.config.xaxis.categories[dataPointIndex];
              const time =w.globals.seriesNames[seriesIndex];
  
              const status=value===-1 ? 'No Data' : value===1 ? 'Usage' : 'No Usage';
              return `
                <div style="padding: 6px; text-align: center;">
                  <div style="font-weight: bold;">${date}</div>
                  <div>${time} - ${status}</div>
                </div>
              `;
            }
          },
          legend: {
            show           : true,
            position       : 'bottom',
            horizontalAlign: 'center',
            labels         : {
              colors         : '#666',
              useSeriesColors: false
            }
          },
          dataLabels: {
            enabled: false
          },
          fill: {
            type: "solid"
          }
        };
  
        this.changeDetectorRef.detectChanges();
      } else {
        this.simActivitySeries=newSeries;
        this.simActivityDates =formattedDates;
  
        this.simActivityChartComponent?.updateSeries(this.simActivitySeries, true);
        this.simActivityChartComponent?.updateOptions({
          xaxis: { categories: this.simActivityDates }
        }, false);
      }
    }
    catch (e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
